import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Carousel, Button, Badge, Spinner } from 'react-bootstrap';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { getProductDepartments } from '../utils/departments';
import SiteNavbar from './SiteNavbar';
import SiteFooter from './SiteFooter';
import ShareButtons from './ShareButtons';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [produto, setProduto]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [added, setAdded]       = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/products/${id}`)
      .then(res => { if (active) setProduto(res.data); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  const handleAdd = () => {
    addToCart(produto);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  const handleBuy = () => {
    addToCart(produto);
    navigate('/checkout');
  };

  const images = (produto?.images || []).filter(Boolean).slice(0, 5);

  // URL absoluta e canônica do produto — é ela que as redes vão buscar para
  // montar o preview com a foto (meta tags servidas por backend/utils/socialMeta.js).
  const shareUrl = produto ? `${window.location.origin}/produto/${produto._id}` : '';

  const imgStyle = { height: 400, width: '100%', objectFit: 'contain', background: '#f7f7f7', borderRadius: 8 };

  return (
    <div>
      <SiteNavbar />
      <Container className="mt-4 mb-5" style={{ maxWidth: 960 }}>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && notFound && (
          <div className="text-center py-5">
            <p className="text-muted">Produto não encontrado.</p>
            <Button variant="warning" onClick={() => navigate('/')}>Voltar à loja</Button>
          </div>
        )}

        {!loading && produto && (
          <>
            <Link to="/" className="text-decoration-none small text-muted">← Voltar à loja</Link>

            <Row className="mt-2 g-4">
              {/* ── Galeria ── */}
              <Col xs={12} md={6}>
                {images.length === 0 && (
                  <div className="d-flex align-items-center justify-content-center"
                    style={{ ...imgStyle, background: '#f0f0f0' }}>
                    <span className="text-muted">Sem imagem</span>
                  </div>
                )}
                {images.length === 1 && (
                  <img src={images[0]} alt={produto.name} style={imgStyle} />
                )}
                {images.length > 1 && (
                  <Carousel interval={null} variant="dark" indicators>
                    {images.map((src, i) => (
                      <Carousel.Item key={i}>
                        <img src={src} alt={`${produto.name} - imagem ${i + 1}`} style={imgStyle} />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                )}
              </Col>

              {/* ── Informações ── */}
              <Col xs={12} md={6}>
                {getProductDepartments(produto).length > 0 && (
                  <div className="mb-2 d-flex flex-wrap gap-1">
                    {getProductDepartments(produto).map(d => (
                      <Badge key={d} bg="light" text="dark" className="border">{d}</Badge>
                    ))}
                  </div>
                )}
                <h1 style={{ fontSize: '1.6rem' }} className="mb-2">{produto.name}</h1>
                {produto.code && (
                  <div className="text-muted small mb-2">Cód.: {produto.code}</div>
                )}
                <p className="fw-bold mb-1" style={{ fontSize: '1.8rem', color: '#333' }}>
                  R$ {produto.price?.toFixed(2).replace('.', ',')}
                </p>
                {typeof produto.stock === 'number' && (
                  <p className={`small mb-3 ${produto.stock > 0 ? 'text-success' : 'text-danger'}`}>
                    {produto.stock > 0 ? `${produto.stock} em estoque` : 'Indisponível no momento'}
                  </p>
                )}

                <div className="d-grid gap-2" style={{ maxWidth: 340 }}>
                  <Button variant="warning" size="lg" onClick={handleBuy}
                    disabled={typeof produto.stock === 'number' && produto.stock <= 0}>
                    Comprar
                  </Button>
                  <Button variant={added ? 'success' : 'outline-secondary'} onClick={handleAdd}
                    disabled={typeof produto.stock === 'number' && produto.stock <= 0}>
                    {added ? 'Adicionado!' : 'Adicionar ao Carrinho'}
                  </Button>
                </div>

                <ShareButtons
                  className="mt-4"
                  url={shareUrl}
                  title={produto.name}
                />
              </Col>
            </Row>

            {/* ── Descrição ── */}
            <hr className="my-4" />
            <h5 className="mb-3">Descrição</h5>
            <p style={{ whiteSpace: 'pre-line', color: '#444', lineHeight: 1.7 }}>
              {produto.description || 'Sem descrição disponível.'}
            </p>
          </>
        )}
      </Container>
      <SiteFooter />
    </div>
  );
}
